import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

/**
 * Tiny wrapper around POST /api/upload that returns the served path
 * (e.g. "/api/files/local-loot/uploads/<uid>/<uuid>.jpg").
 * - value: current photo_url (nullable)
 * - onChange: (newUrl | "") => void
 * - testid: optional data-testid prefix
 */
export default function PhotoUploader({ value, onChange, testid = "photo-uploader" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photo is too large (max 8MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      // Reset the input so the same file can be re-picked.
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => onChange("");

  return (
    <div className="space-y-2" data-testid={testid}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
        data-testid={`${testid}-input`}
      />
      {value ? (
        <div className="flex items-center gap-3">
          <div
            className="w-16 h-16 bg-[#1a1a1a] border border-[#F5F2E8]/15 bg-cover bg-center"
            style={{ backgroundImage: `url(${backend}${value})` }}
            aria-label="Uploaded photo preview"
          />
          <div className="flex items-center gap-2">
            <button type="button" onClick={pick} className="btn-ghost text-xs inline-flex items-center gap-1" disabled={uploading} data-testid={`${testid}-replace`}>
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Replace
            </button>
            <button type="button" onClick={clear} className="btn-ghost text-xs" style={{ color: "#E53935", borderColor: "#E53935" }} data-testid={`${testid}-clear`}>
              <X size={12} className="inline" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          className="btn-ghost text-xs inline-flex items-center gap-2"
          data-testid={`${testid}-pick`}
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {uploading ? "Uploading…" : "Upload photo"}
        </button>
      )}
    </div>
  );
}
